import { v4 as uuidv4 } from 'uuid';
import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { getUrl, uploadData } from '@aws-amplify/storage';
import { APIService } from 'src/app/services/api';
import { UserService } from 'src/app/services/user';
import { LocaleService } from 'src/app/services/locale';
import { EmailService } from 'src/app/services/email.service';
import { InputComponent } from '../../shared/input';
import { DropdownComponent } from '../../shared/dropdown';
import { ButtonComponent } from '../../shared/button';
import { SaveComponent } from '../../icons/save';
import { PlansComponent } from '../../shared/plans';
import { PaymentService } from 'src/app/services/payment';
import { environment } from 'src/environments/environment';
import { UploadComponent } from '../../icons/upload';
import { fetchUserAttributes, fetchAuthSession } from '@aws-amplify/auth';

@Component({
  selector: 'app-profile',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    ButtonComponent,
    SaveComponent,
    PlansComponent,
    UploadComponent,
  ],
  template: `
    <div class="px-2 sm:px-0">
      <!-- Profile Info Section -->
      <div class="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
        <div class="flex items-center gap-3 mb-4 sm:mb-6">
          <div
            class="w-10 h-10 bg-[#043B22]/10 rounded-xl flex items-center justify-center"
          >
            <svg
              class="w-5 h-5 text-[#043B22]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <div>
            <h2 class="text-lg sm:text-xl futura text-[#043B22]">
              {{ locale.profile.heading_profile_info }}
            </h2>
            <p class="text-xs sm:text-sm text-[#616161]">
              {{ locale.profile.subheading_profile_info }}
            </p>
          </div>
        </div>

        <form [formGroup]="form" (submit)="submit()">
          <!-- Profile Photo -->
          <div
            class="mb-6 sm:mb-8 p-4 sm:p-5 bg-gray-50/50 rounded-xl border border-gray-100"
          >
            <div class="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <input
                accept=".jpg, .jpeg, .png"
                #fileInput
                type="file"
                class="hidden"
                (change)="selectFile($event)"
              />
              <div
                [ngStyle]="{
                  backgroundImage: imageUrl
                    ? imageUrl
                    : 'url(/images/welcome.png)',
                }"
                class="h-20 w-20 sm:h-24 sm:w-24 lg:h-28 lg:w-28 bg-center bg-cover rounded-full flex-shrink-0 border-4 border-[#043B22]/10 shadow-lg ring-4 ring-white"
              ></div>
              <div class="flex flex-col gap-2 sm:gap-3">
                <button
                  type="button"
                  (click)="pickFile()"
                  class="px-4 sm:px-5 py-2 sm:py-2.5 border-2 border-[#043B22] text-[#043B22] futura text-xs sm:text-sm flex gap-2 items-center justify-center rounded-xl cursor-pointer hover:bg-[#043B22] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <app-upload></app-upload>
                  {{ locale.form_company_registration.button_upload }}
                </button>
                <p
                  class="text-[10px] sm:text-xs text-[#616161] text-center sm:text-left"
                >
                  JPG, JPEG, PNG (max 5MB)
                </p>
                <p
                  *ngIf="photoError"
                  class="text-[10px] sm:text-xs text-red-500 text-center sm:text-left"
                >
                  {{ photoError }}
                </p>
              </div>
            </div>
          </div>

          <!-- Form Fields -->
          <div
            class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
          >
            <app-input
              label="{{ locale.common.lbl_name }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['name']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_surname }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['surname']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_email }}"
              type="email"
              placeholder=""
              [control]="form.controls['email']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_phone_number }}"
              type="number"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['phoneNumber']"
            ></app-input>
            <app-dropdown
              [options]="[
                {
                  label: locale.register.usertype_option_company,
                  value: 'company',
                },
                {
                  label: locale.register.usertype_option_consultant,
                  value: 'consultant',
                },
                {
                  label: locale.register.usertype_option_site_director,
                  value: 'site_director',
                },
                {
                  label: locale.register.usertype_option_environmental_manager,
                  value: 'environmental_manager',
                },
                {
                  label: locale.register.usertype_option_hse_officer,
                  value: 'hse_officer',
                },
                {
                  label: locale.register.usertype_option_production_supervisor,
                  value: 'production_supervisor',
                },
                {
                  label: locale.register.usertype_option_quality_manager,
                  value: 'quality_manager',
                },
                {
                  label: locale.register.usertype_option_maintenance_lead,
                  value: 'maintenance_lead',
                },
                {
                  label:
                    locale.register.usertype_option_supply_chain_coordinator,
                  value: 'supply_chain_coordinator',
                },
              ]"
              label="{{ locale.common.lbl_company_role }}"
              placeholder="{{ locale.common.placeholder_options }}"
              [control]="form.controls['companyRole']"
            ></app-dropdown>
            <app-input
              label="{{ locale.common.lbl_company_name }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['companyName']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_vat_number }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['vatNumber']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_pec }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['pec']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_street }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['street']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_province }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['province']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_postal_code }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['postalCode']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_city }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['city']"
            ></app-input>
            <app-input
              label="{{ locale.common.lbl_sdi_code }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['sdiCode']"
            ></app-input>
            <app-dropdown
              [options]="[
                {
                  label: locale.common.business_sector_option_trade,
                  value: locale.common.business_sector_option_trade,
                },
                {
                  label: locale.common.business_sector_option_services,
                  value: locale.common.business_sector_option_services,
                },
                {
                  label: locale.common.usertype_option_food_beverage,
                  value: locale.common.usertype_option_food_beverage,
                },
                {
                  label: locale.common.usertype_option_chemical_pharma,
                  value: locale.common.usertype_option_chemical_pharma,
                },
                {
                  label: locale.common.usertype_option_automotive,
                  value: locale.common.usertype_option_automotive,
                },
                {
                  label: locale.common.usertype_option_plastics_rubber,
                  value: locale.common.usertype_option_plastics_rubber,
                },
                {
                  label: locale.common.usertype_option_metal_fabrication,
                  value: locale.common.usertype_option_metal_fabrication,
                },
                {
                  label: locale.common.usertype_option_electronics,
                  value: locale.common.usertype_option_electronics,
                },
                {
                  label: locale.common.usertype_option_textile_apparel,
                  value: locale.common.usertype_option_textile_apparel,
                },
                {
                  label: locale.common.usertype_option_packaging,
                  value: locale.common.usertype_option_packaging,
                },
                {
                  label: locale.common.usertype_option_logistics,
                  value: locale.common.usertype_option_logistics,
                },
                {
                  label: locale.common.usertype_option_other_custom,
                  value: locale.common.usertype_option_other_custom,
                },
              ]"
              label="{{ locale.common.lbl_business_sector }}"
              placeholder="{{ locale.common.placeholder_options }}"
              [control]="form.controls['businessSector']"
            ></app-dropdown>
            <div
              *ngIf="
                form.controls['businessSector'].value ===
                  'Altro (specificare)' ||
                form.controls['businessSector'].value === 'Other (custom)'
              "
            >
              <app-input
                type="text"
                label="{{ locale.common.otherBusinessSector }}"
                [keyUp]="reset"
                placeholder="{{ locale.common.otherBusinessSectorPlaceHolder }}"
                [control]="form.controls['otherBusinessSector']"
              >
              </app-input>
            </div>
            <app-input
              type="number"
              label="{{ locale.common.lbl_no_of_employees }}"
              [keyUp]="reset"
              placeholder="{{ locale.common.placeholder_write_here }}"
              [control]="form.controls['numberOfEmployees']"
            ></app-input>
          </div>

          <!-- Error Message -->
          <div
            *ngIf="error.length"
            class="mt-4 sm:mt-5 p-3 sm:p-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-xs sm:text-sm flex items-center gap-2 sm:gap-3"
          >
            <svg
              class="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              />
            </svg>
            <span>{{ error }}</span>
          </div>

          <!-- Submit Button -->
          <div class="mt-6 sm:mt-8 flex justify-start">
            <app-button
              [disabled]="form.invalid"
              text="{{ locale.profile.button_save }}"
            >
              <app-save></app-save>
            </app-button>
          </div>
        </form>
      </div>

      <!-- Selected Plan Section -->
      <div *ngIf="selectedPlan || stripeSubscription" class="mt-6 sm:mt-8">
        <!-- Section Header -->
        <div class="flex items-center gap-2 mb-4 sm:mb-6">
          <div class="w-1 h-6 sm:h-7 bg-[#043B22] rounded-full"></div>
          <h2 class="text-base sm:text-lg lg:text-xl futura text-[#043B22]">
            {{ locale.common.sp }}
          </h2>
        </div>

        <!-- Loading State -->
        <div
          *ngIf="isLoadingSubscription"
          class="bg-white rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10 border border-gray-100 flex items-center justify-center min-h-[200px]"
        >
          <div class="flex flex-col items-center gap-3 text-[#616161]">
            <svg
              class="animate-spin h-6 w-6 sm:h-8 sm:w-8 text-[#043B22]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span class="text-sm sm:text-base futura">{{
              locale.common.loading || 'Loading...'
            }}</span>
          </div>
        </div>

        <div
          *ngIf="!isLoadingSubscription"
          class="bg-white rounded-2xl shadow-lg overflow-hidden border transition-all duration-300"
          [ngClass]="
            stripeSubscription?.cancel_at_period_end ||
            stripeSubscription?.status === 'canceled'
              ? 'border-orange-200'
              : 'border-gray-100 hover:shadow-xl'
          "
        >
          <div class="flex flex-col lg:flex-row">
            <!-- Plan Image -->
            <div
              class="relative h-40 sm:h-48 lg:h-auto lg:w-56 xl:w-72 flex-shrink-0"
            >
              <img
                src="/images/welcome.png"
                class="object-cover h-full w-full transition-all duration-500"
                [ngClass]="
                  stripeSubscription?.cancel_at_period_end ||
                  stripeSubscription?.status === 'canceled'
                    ? 'grayscale-[30%]'
                    : ''
                "
              />
              <div
                class="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/30 to-transparent"
              ></div>
              <!-- Status Badge
              <div
                class="absolute top-2 sm:top-3 right-2 sm:right-3 lg:left-3 lg:right-auto px-2.5 sm:px-3 py-1 sm:py-1.5 text-white text-[10px] sm:text-xs rounded-full font-medium shadow-lg backdrop-blur-sm"
                [ngClass]="getStatusBadgeClass()"
              >
                {{ getStatusLabel() }}
              </div> -->
            </div>

            <!-- Plan Details -->
            <div class="flex-1 p-4">
              <div class="flex flex-col h-full">
                <!-- Cancelled Warning Banner -->
                <div
                  *ngIf="
                    stripeSubscription?.cancel_at_period_end ||
                    stripeSubscription?.status === 'canceled'
                  "
                  class="mb-4 p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-xl"
                >
                  <div class="flex items-start gap-2 sm:gap-3">
                    <svg
                      class="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <div>
                      <p
                        class="text-xs sm:text-sm font-medium text-orange-800 futura"
                      >
                        {{
                          localeService.getLanguage() === 'english'
                            ? 'Subscription Cancelled'
                            : 'Abbonamento Cancellato'
                        }}
                      </p>
                      <p
                        class="text-[10px] sm:text-xs text-orange-600 mt-0.5 leading-relaxed"
                      >
                        {{
                          localeService.getLanguage() === 'english'
                            ? 'Your subscription has been cancelled. You can continue to use the service until'
                            : 'Il tuo abbonamento è stato cancellato. Puoi continuare a utilizzare il servizio fino al'
                        }}
                        {{ subscriptionEndDate }}.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Plan Name & Price -->
                <div class="mb-4 flex justify-between">
                  <h3
                    class="text-lg sm:text-xl futura text-[#043B22] mb-1 sm:mb-2"
                  >
                    {{
                      selectedPlan?.name ||
                        stripeSubscription?.plan?.nickname ||
                        'Subscription'
                    }}
                  </h3>
                  <p
                    class="text-base sm:text-lg lg:text-xl font-semibold text-[#043B22]"
                  >
                    {{
                      selectedPlan?.price ||
                        formatPrice(
                          stripeSubscription?.plan?.amount,
                          stripeSubscription?.plan?.currency
                        )
                    }}
                    <span class="text-xs sm:text-sm font-normal text-[#616161]"
                      >/
                      {{
                        selectedPlan?.duration ||
                          translateInterval(stripeSubscription?.plan?.interval)
                      }}</span
                    >
                  </p>
                </div>

                <!-- Subscription Info Grid -->
                <div
                  class="grid grid-cols-2 gap-2 sm:gap-3 mb-2"
                  [ngClass]="
                    stripeSubscription?.cancel_at_period_end ||
                    stripeSubscription?.status === 'canceled'
                      ? 'lg:grid-cols-4'
                      : 'lg:grid-cols-3'
                  "
                >
                  <div
                    class="rounded-lg sm:rounded-xl p-4"
                    [ngClass]="
                      stripeSubscription?.status === 'trialing'
                        ? 'bg-blue-50'
                        : 'bg-gray-50'
                    "
                  >
                    <p
                      class="text-[10px] sm:text-xs mb-0.5 sm:mb-1"
                      [ngClass]="
                        stripeSubscription?.status === 'trialing'
                          ? 'text-blue-600'
                          : 'text-[#616161]'
                      "
                    >
                      {{
                        stripeSubscription?.status === 'trialing'
                          ? localeService.getLanguage() === 'english'
                            ? 'Trial Started'
                            : 'Inizio Prova'
                          : locale.common.so || 'Subscribed on'
                      }}
                    </p>
                    <p
                      class="text-xs sm:text-sm lg:text-base font-medium"
                      [ngClass]="
                        stripeSubscription?.status === 'trialing'
                          ? 'text-blue-700'
                          : 'text-[#043B22]'
                      "
                    >
                      {{ subscriptionDate }}
                    </p>
                  </div>
                  <div
                    class="rounded-lg sm:rounded-xl p-4"
                    [ngClass]="
                      stripeSubscription?.cancel_at_period_end ||
                      stripeSubscription?.status === 'canceled'
                        ? 'bg-orange-50'
                        : stripeSubscription?.status === 'trialing'
                          ? 'bg-blue-50'
                          : 'bg-gray-50'
                    "
                  >
                    <p
                      class="text-[10px] sm:text-xs mb-0.5 sm:mb-1"
                      [ngClass]="
                        stripeSubscription?.cancel_at_period_end ||
                        stripeSubscription?.status === 'canceled'
                          ? 'text-orange-600'
                          : stripeSubscription?.status === 'trialing'
                            ? 'text-blue-600'
                            : 'text-[#616161]'
                      "
                    >
                      {{
                        stripeSubscription?.cancel_at_period_end ||
                        stripeSubscription?.status === 'canceled'
                          ? localeService.getLanguage() === 'english'
                            ? 'Expires On'
                            : 'Scade Il'
                          : stripeSubscription?.status === 'trialing'
                            ? localeService.getLanguage() === 'english'
                              ? 'Trial Ends'
                              : 'Fine Prova'
                            : localeService.getLanguage() === 'english'
                              ? 'Valid Until'
                              : 'Valido Fino Al'
                      }}
                    </p>
                    <p
                      class="text-xs sm:text-sm lg:text-base font-medium"
                      [ngClass]="
                        stripeSubscription?.cancel_at_period_end ||
                        stripeSubscription?.status === 'canceled'
                          ? 'text-orange-700'
                          : stripeSubscription?.status === 'trialing'
                            ? 'text-blue-700'
                            : 'text-[#043B22]'
                      "
                    >
                      {{ subscriptionEndDate }}
                    </p>
                  </div>
                  <!-- Cancelled Date (only show when cancelled) -->
                  <div
                    *ngIf="
                      stripeSubscription?.cancel_at_period_end ||
                      stripeSubscription?.status === 'canceled'
                    "
                    class="bg-red-50 rounded-lg sm:rounded-xl p-4"
                  >
                    <p
                      class="text-[10px] sm:text-xs text-red-600 mb-0.5 sm:mb-1"
                    >
                      {{
                        localeService.getLanguage() === 'english'
                          ? 'Cancelled On'
                          : 'Cancellato Il'
                      }}
                    </p>
                    <p
                      class="text-xs sm:text-sm lg:text-base font-medium text-red-700"
                    >
                      {{ cancelledDate }}
                    </p>
                  </div>
                  <div class="bg-gray-50 rounded-lg sm:rounded-xl p-4">
                    <p
                      class="text-[10px] sm:text-xs text-[#616161] mb-0.5 sm:mb-1"
                    >
                      {{
                        localeService.getLanguage() === 'english'
                          ? 'Status'
                          : 'Stato'
                      }}
                    </p>
                    <p
                      class="text-xs sm:text-sm lg:text-base font-medium"
                      [ngClass]="getStatusTextClass()"
                    >
                      {{ getStatusLabel() }}
                    </p>
                  </div>
                </div>

                <!-- Plan Features -->
                <div *ngIf="selectedPlan?.details" class="mb-4 sm:mb-5">
                  <ul class="space-y-1.5 sm:space-y-2">
                    <li
                      *ngFor="let detail of selectedPlan.details"
                      class="flex items-start gap-1.5 sm:gap-2 text-xs sm:text-sm text-[#616161]"
                    >
                      <svg
                        class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#043B22] mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <span>{{ detail }}</span>
                    </li>
                  </ul>
                </div>

                <!-- Cancel Button (only show if not cancelled) -->
                <div
                  *ngIf="
                    !stripeSubscription?.cancel_at_period_end &&
                    (stripeSubscription?.status === 'active' ||
                      stripeSubscription?.status === 'trialing')
                  "
                  class="mt-auto pt-3 sm:pt-4 border-t border-gray-100"
                >
                  <button
                    (click)="showCancelModal = true"
                    class="text-red-600 hover:text-red-700 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 border border-[#e7000b]/80 rounded-lg hover:bg-[#e7000b] hover:text-white cursor-pointer transition-colors bg-white/90"
                  >
                    {{ locale.common.cancelSubscription }}
                  </button>
                </div>

                <!-- Resubscribe prompt (show if cancelled) -->
                <div
                  *ngIf="
                    stripeSubscription?.cancel_at_period_end ||
                    stripeSubscription?.status === 'canceled'
                  "
                  class="mt-auto pt-3 sm:pt-4 border-t border-gray-100"
                >
                  <p class="text-xs sm:text-sm text-[#616161]">
                    {{
                      localeService.getLanguage() === 'english'
                        ? 'Want to continue? Choose a plan below to resubscribe.'
                        : 'Vuoi continuare? Scegli un piano qui sotto per riabbonarti.'
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cancel Subscription Modal -->
      <div
        *ngIf="showCancelModal"
        class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
        (click)="showCancelModal = false"
      >
        <div
          class="bg-white rounded-2xl w-full max-w-[320px] sm:max-w-sm shadow-2xl overflow-hidden transform transition-all"
          (click)="$event.stopPropagation()"
        >
          <!-- Modal Icon -->
          <div class="pt-5 sm:pt-6 pb-3 sm:pb-4 flex justify-center">
            <div
              class="w-14 h-14 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center"
            >
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg shadow-red-500/30"
              >
                <svg
                  class="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <!-- Modal Content -->
          <div class="px-4 sm:px-6 pb-2 text-center">
            <h3
              class="futura text-base sm:text-lg text-[#043B22] mb-1.5 sm:mb-2"
            >
              {{ locale.common.cancelSubscription }}
            </h3>
            <p class="text-xs sm:text-sm text-[#616161] mb-1 leading-relaxed">
              {{ locale.common.cancelSubscriptionConfirm }}
            </p>
            <p class="text-[10px] sm:text-xs text-gray-500">
              {{ locale.common.cancelSubscriptionWarning }}
            </p>
          </div>

          <!-- Modal Actions -->
          <div class="p-4 sm:p-5 pt-3 sm:pt-4 flex gap-2 sm:gap-3">
            <button
              (click)="showCancelModal = false"
              class="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-[#616161] bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl transition-all cursor-pointer"
            >
              {{ locale.common.cancel }}
            </button>
            <button
              (click)="cancelSubscription()"
              class="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-xl transition-all cursor-pointer shadow-md shadow-red-500/30"
            >
              {{ locale.common.confirm }}
            </button>
          </div>
        </div>
      </div>

      <!-- Contract Acceptance Modal -->
      <div
        *ngIf="showContractModal"
        class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
        (click)="showContractModal = false"
      >
        <div
          class="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden transform transition-all max-h-[90vh] flex flex-col"
          (click)="$event.stopPropagation()"
        >
          <!-- Modal Header -->
          <div
            class="bg-gradient-to-r from-[#043B22] to-[#065a35] px-5 py-4 flex-shrink-0"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  ></path>
                </svg>
              </div>
              <div>
                <h3
                  class="text-white futura text-base sm:text-lg font-semibold"
                >
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'Terms & Conditions'
                      : 'Termini e Condizioni'
                  }}
                </h3>
                <p class="text-white/70 text-xs">
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'Please fill in your details and review the contract'
                      : 'Si prega di compilare i dati e leggere il contratto'
                  }}
                </p>
              </div>
            </div>
          </div>

          <!-- Scrollable Modal Content -->
          <div class="overflow-y-auto flex-1 px-5 py-5">
            <!-- Contract Form Fields -->
            <p class="text-sm font-medium text-[#043B22] mb-3">
              {{
                localeService.getLanguage() === 'english'
                  ? 'Company Information'
                  : 'Informazioni Aziendali'
              }}
            </p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <div>
                <label class="block text-xs font-medium text-[#616161] mb-1">
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'Company Name'
                      : 'Ragione Sociale'
                  }}
                  *
                </label>
                <input
                  type="text"
                  [formControl]="contractForm.controls.companyName"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                  [placeholder]="
                    localeService.getLanguage() === 'english'
                      ? 'Company name'
                      : 'Ragione sociale'
                  "
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-[#616161] mb-1">
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'Registered Office'
                      : 'Sede'
                  }}
                  *
                </label>
                <input
                  type="text"
                  [formControl]="contractForm.controls.address"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                  [placeholder]="
                    localeService.getLanguage() === 'english'
                      ? 'Address'
                      : 'Indirizzo sede'
                  "
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-[#616161] mb-1">
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'Tax ID / VAT'
                      : 'C.F. / P.IVA'
                  }}
                  *
                </label>
                <input
                  type="text"
                  [formControl]="contractForm.controls.vatNumber"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                  [placeholder]="
                    localeService.getLanguage() === 'english'
                      ? 'Tax ID / VAT number'
                      : 'Codice fiscale / Partita IVA'
                  "
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-[#616161] mb-1"
                  >PEC *</label
                >
                <input
                  type="email"
                  [formControl]="contractForm.controls.pec"
                  class="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                  [ngClass]="
                    contractForm.controls.pec.touched &&
                    contractForm.controls.pec.invalid
                      ? 'border-red-400'
                      : 'border-gray-300'
                  "
                  [placeholder]="
                    localeService.getLanguage() === 'english'
                      ? 'Certified email (PEC)'
                      : 'Indirizzo PEC'
                  "
                />
                <p
                  *ngIf="
                    contractForm.controls.pec.touched &&
                    contractForm.controls.pec.hasError('email')
                  "
                  class="text-[10px] text-red-500 mt-1"
                >
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'Please enter a valid email address'
                      : 'Inserire un indirizzo email valido'
                  }}
                </p>
              </div>
              <div>
                <label class="block text-xs font-medium text-[#616161] mb-1">
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'Legal Representative'
                      : 'Rappresentante Legale'
                  }}
                  *
                </label>
                <input
                  type="text"
                  [formControl]="contractForm.controls.legalRepresentative"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                  [placeholder]="
                    localeService.getLanguage() === 'english'
                      ? 'Full name'
                      : 'Nome e cognome'
                  "
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-[#616161] mb-1">
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'Place and Date'
                      : 'Luogo e Data'
                  }}
                </label>
                <input
                  type="text"
                  [value]="getContractPlaceAndDate()"
                  disabled
                  class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-[#616161]"
                />
              </div>
            </div>

            <!-- Dynamic Contract Text -->
            <div
              class="border border-gray-200 rounded-xl p-4 sm:p-6 bg-gray-50/50 mb-5 text-[11px] sm:text-xs leading-relaxed text-[#333]"
            >
              <p
                class="text-center text-[10px] text-[#616161] uppercase tracking-wider mb-1"
              >
                FOREVER BAMBU' SPA SOCIETA' BENEFIT
              </p>
              <p class="text-center text-[10px] text-[#616161] mb-4">
                SS11 Padana Superiore 16/18 20063 Cernusco sul Naviglio (MI)<br />P.iva
                12072470961 | info&#64;foreverbambu.com | +39 02 40031483
              </p>

              <h4 class="text-center font-bold text-sm text-[#043B22] mb-1">
                CONDIZIONI GENERALI DI CONTRATTO
              </h4>
              <h4 class="text-center font-bold text-sm text-[#043B22] mb-4">
                SOFTWARE CARBON FOOTPRINT PRO
              </h4>

              <p class="text-center mb-4">Con la presente scrittura privata,</p>

              <p class="font-semibold mb-2">TRA</p>
              <p class="mb-4">
                FOREVER BAMBU S.P.A. SOCIETA BENEFIT (di seguito denominata
                "FOREVER BAMBU" e/o "Fornitore"), con sede legale in SS11 Padana
                Superiore 16/18 - 20063 Cernusco sul Naviglio (MI), P.IVA
                12072470961, PEC: foreverbambu29&#64;pec.it, legalmente
                rappresentata dall'Amministratore Delegato.
              </p>

              <p class="font-semibold mb-2">E</p>
              <p class="mb-2">La Societa (di seguito denominata "Cliente"):</p>
              <p class="mb-1">
                RAGIONE SOCIALE:
                <span
                  class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                  >{{
                    contractForm.controls.companyName.value || '_______________'
                  }}</span
                >
              </p>
              <p class="mb-1">
                SEDE:
                <span
                  class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                  >{{
                    contractForm.controls.address.value || '_______________'
                  }}</span
                >
              </p>
              <p class="mb-1">
                C.F./P.IVA:
                <span
                  class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                  >{{
                    contractForm.controls.vatNumber.value || '_______________'
                  }}</span
                >
              </p>
              <p class="mb-1">
                PEC:
                <span
                  class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                  >{{
                    contractForm.controls.pec.value || '_______________'
                  }}</span
                >
              </p>
              <p class="mb-2">
                nella persona del suo legale rappresentante
                <span
                  class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                  >{{
                    contractForm.controls.legalRepresentative.value ||
                      '_______________'
                  }}</span
                >
              </p>
              <p class="mb-4">
                (di seguito congiuntamente "Parti" e disgiuntamente "Parte").
              </p>

              <hr class="my-4 border-gray-300" />

              <p class="font-bold mb-2">Definizioni</p>
              <ul class="list-disc pl-5 space-y-1 mb-4">
                <li>
                  <strong>"Software"</strong>: il programma informatico
                  denominato "Carbon Footprint Pro", sviluppato e di proprieta
                  esclusiva di FOREVER BAMBU, fornito in modalita SaaS (Software
                  as a Service) e ospitato su infrastruttura cloud.
                </li>
                <li>
                  <strong>"Servizio"</strong>: l'accesso, l'uso del Software e
                  degli eventuali moduli aggiuntivi, secondo le modalita
                  previste nel presente Contratto.
                </li>
                <li>
                  <strong>"Cliente"</strong>: il soggetto (persona giuridica o
                  professionista) che sottoscrive il presente Contratto e che
                  utilizza il Software.
                </li>
                <li>
                  <strong>"Credenziali di Accesso"</strong>: combinazione di
                  URL, username e password necessarie per accedere al Software.
                </li>
                <li>
                  <strong>"Manutenzione Ordinaria"</strong>: interventi
                  finalizzati a correggere errori e garantire la continuita del
                  servizio.
                </li>
                <li>
                  <strong>"Manutenzione Straordinaria"</strong>: interventi
                  finalizzati a migliorare funzionalita, sicurezza e performance
                  del Software, nonche ad adeguarlo a modifiche normative.
                </li>
                <li>
                  <strong>"Assistenza"</strong>: supporto tecnico e applicativo
                  fornito da FOREVER BAMBU secondo i termini del presente
                  Contratto.
                </li>
                <li>
                  <strong>"Ordine"</strong>: il documento sottoscritto dal
                  Cliente che definisce il canone, la durata, i moduli
                  acquistati e le eventuali configurazioni personalizzate.
                </li>
              </ul>
              <p class="mb-4">
                Le Definizioni descritte costituiscono parte integrante del
                presente Contratto.
              </p>

              <p class="font-bold mb-2">Art. 1 - Oggetto del Contratto</p>
              <p class="mb-4">
                Il presente Contratto disciplina le condizioni di utilizzo del
                Software "Carbon Footprint Pro", fornito da FOREVER BAMBU in
                modalita SaaS. Il Cliente ottiene il diritto non esclusivo, non
                trasferibile e limitato nel tempo ad accedere e utilizzare il
                Software, per le proprie esigenze aziendali, nel rispetto delle
                presenti condizioni. Il Contratto include l'attivazione del
                servizio, la concessione delle Credenziali di Accesso e la
                fornitura della Manutenzione Ordinaria e Straordinaria per tutta
                la durata contrattuale.
              </p>

              <p class="font-bold mb-2">Art. 2 - Modalita di Utilizzo</p>
              <p class="mb-4">
                Il Cliente potra accedere al Software esclusivamente tramite le
                Credenziali di Accesso fornite da FOREVER BAMBU. E vietata
                qualsiasi forma di cessione, sublicenza, copia, reverse
                engineering, decompilazione o utilizzo difforme rispetto alla
                finalita del Software. Il Cliente potra consentire l'uso del
                Software unicamente ai propri dipendenti e collaboratori,
                rimanendo responsabile per il loro operato.
              </p>

              <p class="font-bold mb-2">
                Art. 3 - Diritti e Obblighi del Cliente
              </p>
              <p class="mb-2">
                Il Cliente e tenuto ad utilizzare il Software con la massima
                diligenza, nel rispetto della normativa vigente e delle presenti
                condizioni contrattuali. E fatto divieto di:
              </p>
              <ul class="list-disc pl-5 space-y-1 mb-2">
                <li>utilizzare il Software per finalita illecite;</li>
                <li>
                  archiviare, pubblicare o trasmettere contenuti che violino
                  diritti di proprieta intellettuale, norme di legge, privacy o
                  che siano diffamatori, osceni o lesivi di terzi;
                </li>
                <li>
                  trasmettere virus, malware o contenuti che possano
                  compromettere la sicurezza del sistema.
                </li>
              </ul>
              <p class="mb-4">
                Il Cliente manleva FOREVER BAMBU da ogni responsabilita per
                l'uso improprio del Software e da eventuali richieste
                risarcitorie di terzi derivanti da tali comportamenti. FOREVER
                BAMBU potra sospendere immediatamente l'accesso in caso di
                violazioni gravi.
              </p>

              <p class="font-bold mb-2">
                Art. 4 - Corrispettivo e Modalita di Pagamento
              </p>
              <p class="mb-2">
                Il Cliente corrispondera a FOREVER BAMBU un canone di
                abbonamento annuale o mensile, come indicato nell'Ordine. Il
                pagamento e anticipato e costituisce condizione necessaria per
                l'attivazione del servizio.
              </p>
              <p class="mb-2">
                Il canone comprende l'utilizzo del Software, la Manutenzione
                Ordinaria e Straordinaria e l'accesso agli aggiornamenti. Sono
                escluse eventuali attivita di consulenza, formazione,
                personalizzazione o integrazione con sistemi terzi, che saranno
                oggetto di offerte separate.
              </p>
              <p class="mb-2">
                In caso di mancato pagamento entro 30 (trenta) giorni dalla
                scadenza, FOREVER BAMBU potra sospendere l'accesso al Software
                fino alla regolarizzazione.
              </p>
              <p class="mb-4">
                Il canone potra essere aggiornato annualmente in base all'indice
                FOI ISTAT, con preavviso scritto di 60 giorni.
              </p>

              <p class="font-bold mb-2">
                Art. 5 - Manutenzione, Aggiornamenti e Assistenza
              </p>
              <p class="mb-2">
                FOREVER BAMBU garantira la Manutenzione Ordinaria per correggere
                eventuali malfunzionamenti segnalati dal Cliente. La
                Manutenzione Straordinaria, volta a migliorare e aggiornare il
                Software, sara implementata automaticamente e senza costi
                aggiuntivi.
              </p>
              <p class="font-semibold mb-1">Livelli di Servizio (SLA):</p>
              <ul class="list-disc pl-5 space-y-1 mb-2">
                <li>Disponibilita minima garantita: 99,5% su base annuale.</li>
                <li>
                  Tempo di risposta ticket: Alta priorita: 4 ore lavorative;
                  Media priorita: 8 ore lavorative; Bassa priorita: 24 ore
                  lavorative.
                </li>
              </ul>
              <p class="font-semibold mb-1">Service Credit:</p>
              <ul class="list-disc pl-5 space-y-1 mb-2">
                <li>
                  &lt; 99,5% ma &ge; 99%: credito pari al 10% del canone annuo.
                </li>
                <li>
                  &lt; 99% ma &ge; 98%: credito pari al 25% del canone annuo.
                </li>
                <li>&lt; 98%: credito pari al 50% del canone annuo.</li>
              </ul>
              <p class="mb-4">
                Il Cliente dovra fornire informazioni dettagliate sulle anomalie
                per consentire diagnosi e risoluzione tempestiva.
              </p>

              <p class="font-bold mb-2">Art. 6 - Durata e Rinnovo</p>
              <p class="mb-2">
                Il presente Contratto ha durata minima di 12 (dodici) mesi dalla
                data di attivazione del Software. Alla scadenza si rinnova
                tacitamente per ulteriori 12 mesi, salvo disdetta scritta
                tramite PEC almeno 90 (novanta) giorni prima della scadenza.
              </p>
              <p class="mb-4">
                Il Cliente puo recedere senza penali entro 30 giorni dalla
                ricezione di modifiche sostanziali a prezzo, SLA o condizioni
                contrattuali.
              </p>

              <p class="font-bold mb-2">Art. 7 - Sospensione e Risoluzione</p>
              <p class="mb-2">
                FOREVER BAMBU potra sospendere il servizio in caso di mancato
                pagamento, di gravi violazioni contrattuali o per motivi di
                sicurezza. La sospensione sara comunicata via PEC con
                indicazione dei tempi di riattivazione.
              </p>
              <p class="mb-4">
                In caso di risoluzione, il Cliente dovra esportare i propri dati
                entro 30 (trenta) giorni; successivamente FOREVER BAMBU
                provvedera alla cancellazione definitiva di dati e backup.
              </p>

              <p class="font-bold mb-2">Art. 8 - Proprieta Intellettuale</p>
              <p class="mb-4">
                Il Software, i codici sorgente e ogni diritto di proprieta
                intellettuale restano di esclusiva titolarita di FOREVER BAMBU.
                Il Cliente acquisisce solo un diritto d'uso limitato, personale
                e non trasferibile.
              </p>

              <p class="font-bold mb-2">Art. 9 - Input e Output</p>
              <p class="mb-2">
                Il Cliente mantiene la titolarita sugli Input (dati caricati) e
                sugli Output generati dal Software. FOREVER BAMBU potra
                utilizzare dati anonimizzati per finalita statistiche e di
                miglioramento del servizio, senza possibilita di identificazione
                del Cliente.
              </p>
              <p class="mb-4">
                Gli Output hanno finalita informativa e non costituiscono
                consulenza professionale vincolante.
              </p>

              <p class="font-bold mb-2">
                Art. 10 - Responsabilita e Indennizzo
              </p>
              <p class="mb-2">
                La responsabilita complessiva di FOREVER BAMBU e limitata al 50%
                del canone annuo corrisposto, salvo dolo o colpa grave.
              </p>
              <p class="mb-2">
                Il Cliente manleva FOREVER BAMBU per danni derivanti da uso
                improprio del Software o da violazione di diritti di terzi.
              </p>
              <p class="mb-4">
                FOREVER BAMBU manlevera il Cliente per eventuali azioni di terzi
                per violazione di diritti IP derivanti dall'uso conforme del
                Software.
              </p>

              <p class="font-bold mb-2">
                Art. 11 - Protezione dei Dati e Sicurezza
              </p>
              <p class="mb-2">
                FOREVER BAMBU trattera i dati personali in qualita di
                responsabile ai sensi del GDPR.
              </p>
              <p class="mb-2">
                In caso di data breach, FOREVER BAMBU notifichera il Cliente
                entro 24 ore dalla scoperta per consentire l'adempimento
                dell'obbligo di notifica entro 72 ore previsto dal GDPR.
              </p>
              <p class="mb-4">
                Sono adottate misure tecniche e organizzative adeguate:
                crittografia, backup giornaliero con retention di 14 giorni,
                logging degli accessi e segregazione dei dati per cliente.
              </p>

              <p class="font-bold mb-2">Art. 12 - Subprocessor</p>
              <p class="mb-4">
                FOREVER BAMBU potra avvalersi di subprocessor per l'erogazione
                del servizio (es. provider cloud). L'elenco aggiornato e
                disponibile su richiesta. FOREVER BAMBU notifichera modifiche
                sostanziali all'elenco con congruo preavviso e rimarra
                responsabile per le attivita dei subprocessor.
              </p>

              <p class="font-bold mb-2">Art. 13 - Audit e Conformita</p>
              <p class="mb-4">
                Il Cliente potra richiedere audit di sicurezza previo preavviso
                e in accordo con FOREVER BAMBU, senza compromettere la sicurezza
                e la riservatezza di altri clienti.
              </p>

              <p class="font-bold mb-2">Art. 14 - Beta e Servizi in Preview</p>
              <p class="mb-4">
                Funzionalita Beta o in Preview saranno fornite "as is" e
                potranno essere escluse da SLA e indennizzi.
              </p>

              <p class="font-bold mb-2">
                Art. 15 - Legge Applicabile e Foro Competente
              </p>
              <p class="mb-4">
                Il presente Contratto e regolato dalla legge italiana. Per
                qualsiasi controversia e competente in via esclusiva il Foro di
                Milano.
              </p>

              <hr class="my-4 border-gray-300" />

              <p class="mb-2">
                Luogo e Data:
                <span
                  class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                  >{{ getContractPlaceAndDate() }}</span
                >
              </p>
              <p class="mb-4">Per accettazione</p>
              <p class="mb-2">
                Il Cliente
                <span
                  class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                  >{{
                    contractForm.controls.companyName.value || '_______________'
                  }}</span
                >
                (Timbro e Firma)
              </p>
              <p>
                FOREVER BAMBU S.P.A. SOCIETA BENEFIT ___________________________
                (Firma)
              </p>
            </div>

            <!-- Checkbox -->
            <label
              class="flex items-start gap-3 cursor-pointer select-none"
              (click)="contractAccepted = !contractAccepted"
            >
              <div
                class="w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all"
                [ngClass]="
                  contractAccepted
                    ? 'bg-[#043B22] border-[#043B22]'
                    : 'border-gray-300 hover:border-[#043B22]'
                "
              >
                <svg
                  *ngIf="contractAccepted"
                  class="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
              <span class="text-sm text-[#333]">
                {{
                  localeService.getLanguage() === 'english'
                    ? 'I have read and accept the General Terms and Conditions'
                    : 'Ho letto e accetto le Condizioni Generali di Contratto'
                }}
              </span>
            </label>
          </div>

          <!-- Modal Actions -->
          <div
            class="px-5 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0"
          >
            <button
              (click)="showContractModal = false"
              class="flex-1 px-4 py-2.5 text-sm font-medium text-[#616161] bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl transition-all cursor-pointer"
            >
              {{
                localeService.getLanguage() === 'english' ? 'Cancel' : 'Annulla'
              }}
            </button>
            <button
              (click)="confirmSubscription()"
              [disabled]="!contractAccepted || contractForm.invalid"
              class="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-all flex items-center justify-center gap-2"
              [ngClass]="
                contractAccepted && contractForm.valid
                  ? 'bg-[#043B22] hover:bg-[#065a35] cursor-pointer shadow-md'
                  : 'bg-gray-300 cursor-not-allowed'
              "
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                ></path>
              </svg>
              {{
                localeService.getLanguage() === 'english'
                  ? 'Proceed to Payment'
                  : 'Procedi al Pagamento'
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div *ngIf="selectedPlan" class="my-6 sm:my-8 lg:my-10"></div>

      <!-- Plans Component -->
      <app-plans
        [profile]="true"
        [planType]="currentRole === 'company' ? 'Corporate' : 'Consultant'"
        [selectPlan]="selectPlan"
        [selectedPlan]="selectedPlan"
      ></app-plans>

      <!-- Subscription History Section -->
      <div *ngIf="subscriptionHistory.length > 0" class="mt-6 sm:mt-8 lg:mt-10">
        <div
          class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl"
        >
          <!-- Section Header -->
          <div
            class="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-[#043B22]/10 rounded-xl flex items-center justify-center"
              >
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-[#043B22]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
              </div>
              <div>
                <h2
                  class="text-base sm:text-lg lg:text-xl futura text-[#043B22]"
                >
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'Subscription History'
                      : 'Storico Abbonamenti'
                  }}
                </h2>
                <p class="text-xs sm:text-sm text-[#616161]">
                  {{
                    localeService.getLanguage() === 'english'
                      ? 'View your past and current subscriptions'
                      : 'Visualizza i tuoi abbonamenti passati e attuali'
                  }}
                </p>
              </div>
            </div>
          </div>

          <!-- Mobile Card View -->
          <div class="block sm:hidden divide-y divide-gray-100">
            <div
              *ngFor="let item of subscriptionHistory"
              class="p-4 hover:bg-gray-50/50 transition-colors"
            >
              <div class="flex items-start justify-between mb-3">
                <div>
                  <p class="text-sm font-medium text-[#043B22] futura">
                    {{ getSubscriptionPlanName(item) }}
                  </p>
                  <p class="text-xs text-[#616161] mt-0.5">
                    {{ getSubscriptionAmount(item) }}
                  </p>
                </div>
                <span
                  class="px-2 py-0.5 text-[10px] font-medium rounded-full"
                  [ngClass]="{
                    'bg-green-100 text-green-700':
                      item.status === 'active' && !item.cancel_at_period_end,
                    'bg-orange-100 text-orange-700':
                      item.status === 'canceled' || item.cancel_at_period_end,
                    'bg-red-100 text-red-700':
                      item.status === 'past_due' || item.status === 'unpaid',
                    'bg-gray-100 text-gray-700':
                      item.status === 'incomplete' ||
                      item.status === 'trialing',
                  }"
                >
                  {{ getHistoryStatusLabel(item) }}
                </span>
              </div>
              <div class="flex items-center gap-1.5 text-[10px] text-[#616161]">
                <svg
                  class="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span
                  >{{ getSubscriptionStartDate(item) }} -
                  {{ getSubscriptionEndDate(item) }}</span
                >
              </div>
            </div>
          </div>

          <!-- Desktop Table View -->
          <div class="hidden sm:block overflow-x-auto">
            <table class="w-full">
              <thead class="bg-[#043B22] text-white border-b border-gray-100">
                <tr>
                  <th
                    class="px-4 lg:px-6 py-3 lg:py-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider"
                  >
                    {{
                      localeService.getLanguage() === 'english'
                        ? 'Plan'
                        : 'Piano'
                    }}
                  </th>
                  <th
                    class="px-4 lg:px-6 py-3 lg:py-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider"
                  >
                    {{
                      localeService.getLanguage() === 'english'
                        ? 'Amount'
                        : 'Importo'
                    }}
                  </th>
                  <th
                    class="px-4 lg:px-6 py-3 lg:py-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider"
                  >
                    {{
                      localeService.getLanguage() === 'english'
                        ? 'Period'
                        : 'Periodo'
                    }}
                  </th>
                  <th
                    class="px-4 lg:px-6 py-3 lg:py-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider"
                  >
                    {{
                      localeService.getLanguage() === 'english'
                        ? 'Status'
                        : 'Stato'
                    }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  *ngFor="let item of subscriptionHistory"
                  class="hover:bg-gray-50/50 transition-colors"
                >
                  <td
                    class="px-4 lg:px-6 py-3 lg:py-4 text-xs sm:text-sm text-[#043B22] font-medium"
                  >
                    {{ getSubscriptionPlanName(item) }}
                  </td>
                  <td
                    class="px-4 lg:px-6 py-3 lg:py-4 text-xs sm:text-sm text-[#616161]"
                  >
                    {{ getSubscriptionAmount(item) }}
                  </td>
                  <td
                    class="px-4 lg:px-6 py-3 lg:py-4 text-xs sm:text-sm text-[#616161]"
                  >
                    {{ getSubscriptionStartDate(item) }} -
                    {{ getSubscriptionEndDate(item) }}
                  </td>
                  <td class="px-4 lg:px-6 py-3 lg:py-4">
                    <span
                      class="px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full"
                      [ngClass]="{
                        'bg-green-100 text-green-700':
                          item.status === 'active' &&
                          !item.cancel_at_period_end,
                        'bg-orange-100 text-orange-700':
                          item.status === 'canceled' ||
                          item.cancel_at_period_end,
                        'bg-red-100 text-red-700':
                          item.status === 'past_due' ||
                          item.status === 'unpaid',
                        'bg-gray-100 text-gray-700':
                          item.status === 'incomplete' ||
                          item.status === 'trialing',
                      }"
                    >
                      {{ getHistoryStatusLabel(item) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class ProfileComponent {
  @ViewChild('fileInput') fileInput!: ElementRef;

  private api = inject(APIService);
  private spinner = inject(NgxSpinnerService);
  private toastr = inject(ToastrService);
  private userService = inject(UserService);
  private emailService = inject(EmailService);
  private router = inject(Router);
  localeService = inject(LocaleService);
  private paymentService = inject(PaymentService);

  locale = this.localeService.locale();

  form = new FormGroup({
    id: new FormControl(''),
    name: new FormControl('', Validators.required),
    surname: new FormControl('', Validators.required),
    email: new FormControl({ value: '', disabled: true }),
    phoneNumber: new FormControl('', [
      Validators.minLength(10),
      Validators.maxLength(10),
    ]),
    companyRole: new FormControl('', Validators.required),
    companyName: new FormControl(''),
    vatNumber: new FormControl(''),
    address: new FormControl(''),
    street: new FormControl(''),
    postalCode: new FormControl(''),
    city: new FormControl(''),
    province: new FormControl(''),
    country: new FormControl('Italia'),
    businessSector: new FormControl(''),
    otherBusinessSector: new FormControl(''),
    numberOfEmployees: new FormControl(''),
    profilePhoto: new FormControl(''),
    pec: new FormControl(''),
    legalRepresentative: new FormControl(''),
    sdiCode: new FormControl(''),
  });

  userEmail: string = '';

  error = '';

  selectedPlan: any = '';
  subscribed: any = null;
  subscriptionDate: string = '';
  subscriptionEndDate: string = '';
  cancelledDate: string = '';
  showCancelModal = false;
  showContractModal = false;
  contractAccepted = false;
  pendingPlanId: string = '';
  contractForm = new FormGroup({
    companyName: new FormControl('', Validators.required),
    address: new FormControl('', Validators.required),
    vatNumber: new FormControl('', Validators.required),
    pec: new FormControl('', [Validators.required, Validators.email]),
    legalRepresentative: new FormControl('', Validators.required),
  });

  // Stripe subscription data
  stripeSubscription: any = null;
  subscriptionHistory: any[] = [];
  isLoadingSubscription = false;

  currentRole: any = null;
  currentUser = this.userService.user();

  imageUrl: string | null = null;
  selectedFile: File | null = null;

  // Flag to prevent multiple subscription fetches
  private subscriptionFetched = false;
  // Track which user ID we've loaded to prevent duplicate loads
  private loadedUserId: string | null = null;

  constructor(private route: ActivatedRoute) {
    this.form.get('companyRole')?.disable();
    effect(async () => {
      this.locale = this.localeService.locale();
      this.currentUser = this.userService.user();
      if (!this.currentUser?.id) {
        return;
      }

      // Always re-lookup selectedPlan when language changes (locale is reactive)
      // @ts-ignore
      this.subscribed = this.currentUser?.subscriptionPlan;
      // @ts-ignore
      if (this.currentUser?.subscriptionPlan?.length > 0) {
        const found = this.paymentService
          .getAllPlans()
          .find((x) => x?.id === this.subscribed);
        if (found) {
          this.selectedPlan = found;
        }
      }

      // Skip if we've already loaded this user's data
      if (this.loadedUserId === this.currentUser.id) {
        return;
      }
      this.loadedUserId = this.currentUser.id;

      this.imageUrl = `url("${await this.getS3Url(this.currentUser)}")`;

      // Fetch user email from Cognito
      const userAttributes = await fetchUserAttributes();
      this.userEmail = userAttributes.email || '';

      // Parse structured address JSON if stored, otherwise treat as legacy plain string
      const rawAddress = (this.currentUser as any)?.address ?? '';
      let street = '',
        postalCode = '',
        city = '',
        province = '',
        country = 'Italia',
        sdiCode = '';
      try {
        const parsedAddr = JSON.parse(rawAddress);
        if (parsedAddr && typeof parsedAddr === 'object') {
          street = parsedAddr.street || '';
          postalCode = parsedAddr.postalCode || '';
          city = parsedAddr.city || '';
          province = parsedAddr.province || '';
          country = parsedAddr.country || 'Italia';
          sdiCode = parsedAddr.sdiCode || '';
        }
      } catch (e) {
        // Legacy plain-string address: use as street fallback
        street = rawAddress;
      }

      this.form.setValue({
        // @ts-ignore
        id: this.currentUser?.id ?? '', // @ts-ignore
        name: this.currentUser?.name ?? '', // @ts-ignore
        surname: this.currentUser?.surname ?? '',
        email: this.userEmail, // @ts-ignore
        phoneNumber: this.currentUser?.phoneNumber ?? '', // @ts-ignore
        companyRole: this.currentUser?.companyRole ?? '', // @ts-ignore
        companyName: this.currentUser?.companyName ?? '', // @ts-ignore
        vatNumber: this.currentUser?.vatNumber ?? '', // @ts-ignore
        address: rawAddress, // keep raw value for backward compat
        street,
        postalCode,
        city,
        province,
        country,
        businessSector: this.currentUser?.businessSector ?? '',
        otherBusinessSector: this.currentUser?.otherBusinessSector ?? '',
        numberOfEmployees:
          // @ts-ignore
          this.currentUser?.numberOfEmployees?.toString() ?? '', // @ts-ignore
        profilePhoto: this.currentUser?.profilePhoto ?? '',
        pec: this.currentUser?.pec ?? '',
        legalRepresentative: this.currentUser?.legalRepresentative ?? '',
        sdiCode,
      });
      this.form.controls['businessSector'].valueChanges.subscribe((value) => {
        if (value !== 'Altro (specificare)' && value !== 'Other (custom)') {
          this.form.controls['otherBusinessSector'].setValue('');
        }
      });
      // Fetch subscription data from Stripe (only once per user)
      if (!this.subscriptionFetched) {
        this.subscriptionFetched = true;
        this.fetchStripeSubscription();
      }
      // @ts-ignore
      this.currentRole = this.currentUser?.companyRole;
    });

    this.route.queryParamMap.subscribe(async (params) => {
      const subscribed = params.get('subscribed');
      const id = params.get('id');

      if (!id) return;

      if (subscribed === 'n') {
        this.toastr.error(this.locale.common.su);
        // Clear URL parameters to prevent re-processing on page refresh
        this.router.navigate(['/dashboard/profile'], { replaceUrl: true });
      } else if (subscribed && subscribed.length > 0) {
        await this.api.UpdateUserDetails({
          id: id,
          subscriptionPlan: subscribed,
        });

        await this.userService.getUser();

        // Cancel old subscriptions (keep only the newest one)
        try {
          const userEmail = (await fetchUserAttributes()).email;
          if (userEmail) {
            const session = await fetchAuthSession();
            const authToken = session.tokens?.idToken?.toString();
            if (authToken) {
              console.log('Calling cancel_old_subscriptions for:', userEmail);
              const cancelResponse = await fetch(
                environment.lambdaUrls.stripe,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                  },
                  body: JSON.stringify({
                    email: userEmail,
                    action: 'cancel_old_subscriptions',
                  }),
                },
              );
              const cancelResult = await cancelResponse.json();
              console.log('Cancel old subscriptions result:', cancelResult);

              // Refresh subscription data to show updated status after cancellation
              await this.fetchStripeSubscription();
            }

            // Send emails for new subscription (non-blocking, fire and forget)
            const plan = this.paymentService
              .getAllPlans()
              .find((p: any) => p.id === subscribed);
            if (plan) {
              const firstName =
                this.currentUser?.name ||
                this.form.controls['name'].value ||
                '';
              const lastName =
                this.currentUser?.surname ||
                this.form.controls['surname'].value ||
                '';
              const nextBillingDate = new Date();
              nextBillingDate.setMonth(
                nextBillingDate.getMonth() +
                  (plan.duration === 'year' ? 12 : 1),
              );

              // Existing: Subscription purchase confirmation
              this.emailService.sendSubscriptionPurchaseEmail(userEmail, {
                planName: plan.name,
                amount: plan.price,
                billingPeriod: plan.duration,
                nextBillingDate: nextBillingDate.toLocaleDateString(),
              });

              // New Email #1: Internal enrollment notification to Forever Bambù
              this.emailService.sendEnrollmentNotification({
                firstName,
                lastName,
                userEmail,
                planName: plan.name,
              });

              // New Email #2: CFPro activation with training course info to customer
              this.emailService.sendCfproActivationEmail(userEmail, {
                firstName,
                lastName,
                planName: plan.name,
              });

              // Course director notification for Pro/Master plans (includes training)
              if ((plan as any).videosIncluded) {
                this.emailService.sendCourseDirectorNotification({
                  companyName:
                    this.currentUser?.companyName ||
                    this.form.controls['companyName'].value ||
                    'N/A',
                  vatNumber:
                    this.currentUser?.vatNumber ||
                    this.form.controls['vatNumber'].value ||
                    'N/A',
                  contactEmail: userEmail,
                  contactPhone:
                    this.currentUser?.phoneNumber ||
                    this.form.controls['phoneNumber'].value ||
                    'N/A',
                  planName: plan.name,
                  purchaseDateTime: new Date().toLocaleString('it-IT', {
                    timeZone: 'Europe/Rome',
                  }),
                  orderId: this.stripeSubscription?.id || 'N/A',
                  subscriptionId: this.stripeSubscription?.id || 'N/A',
                });
              }
            }
          }
        } catch (cancelError) {
          console.error('Failed to cancel old subscriptions:', cancelError);
        }

        this.toastr.success(this.locale.common.ss);

        // Clear URL parameters to prevent re-processing on page refresh
        this.router.navigate(['/dashboard/profile'], { replaceUrl: true });
      }
    });
  }

  getS3Url = async (currentUser: any) => {
    try {
      const fileUrl = await getUrl({ path: currentUser.profilePhoto });
      return fileUrl.url.toString();
    } catch (error) {
      return '/images/welcome.png';
    }
  };

  submit = async () => {
    try {
      this.spinner.show();
      await this.uploadLogo();

      if (!this.form.value.id) {
        const newId = this.generateId();
        this.form.controls['id'].setValue(newId);
        await this.userService.createUser(this.form);
      } else {
        await this.userService.updateUser(this.form);
      }

      localStorage.setItem(
        'companyDetails',
        JSON.stringify(this.form.getRawValue()),
      );

      this.toastr.success(this.locale.common.profileUpdated);
    } catch (error) {
      this.error = (error as any).message;
      this.toastr.error(this.locale.common.profileUpdateFailed);
    } finally {
      this.spinner.hide();
    }
  };
  private generateId(): string {
    try {
      return uuidv4();
    } catch (e) {
      return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }
  }
  pickFile(): void {
    this.fileInput.nativeElement.click();
  }

  photoError = '';

  selectFile(event: any): void {
    const file = event.target.files[0];

    if (file) {
      const maxSizeBytes = 5 * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        this.photoError = 'Maximum file size allowed is 5MB.';
        event.target.value = '';
        return;
      }

      this.photoError = '';
      this.selectedFile = file;

      const reader = new FileReader();

      reader.onload = async (e: any) => {
        this.imageUrl = "url('" + e.target.result + "')";
      };

      reader.readAsDataURL(file);
    }
  }

  async uploadLogo(): Promise<void> {
    try {
      if (!this.selectedFile) return;

      const fileName = `profile-photos/${Date.now()}_${this.selectedFile.name}`;

      await uploadData({
        path: fileName,
        data: this.selectedFile,
      }).result;

      this.form.controls['profilePhoto'].setValue(fileName);
    } catch (error) {}
  }

  reset = () => {
    this.error = '';
  };

  fetchStripeSubscription = async () => {
    try {
      this.isLoadingSubscription = true;
      const userEmail = (await fetchUserAttributes()).email;

      // Get Cognito auth token
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      if (!authToken) {
        console.error('Not authenticated');
        return;
      }

      const response = await fetch(environment.lambdaUrls.stripe, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          email: userEmail,
          action: 'get_subscription',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Stripe subscription data:', data);
        this.stripeSubscription = data.subscription;

        // Use history array from Lambda (contains all subscriptions)
        if (data.history && data.history.length > 0) {
          this.subscriptionHistory = data.history;
          // If subscription is null, use the most recent active/trialing/cancelled-but-not-expired subscription from history
          if (!this.stripeSubscription) {
            const now = Math.floor(Date.now() / 1000);
            const activeSub = data.history.find(
              (s: any) =>
                s.status === 'active' ||
                s.status === 'trialing' ||
                (s.status === 'canceled' &&
                  s.current_period_end &&
                  s.current_period_end > now),
            );
            if (activeSub) {
              this.stripeSubscription = activeSub;
            }
          }
        } else if (data.subscription) {
          // Fallback to single subscription if history not available
          this.subscriptionHistory = [data.subscription];
        } else {
          this.subscriptionHistory = [];
        }
        console.log('subscriptionHistory', this.subscriptionHistory);
        // Update subscription dates from Stripe data
        if (this.stripeSubscription) {
          // Handle both timestamp (seconds) and date string formats
          // current_period_start/end can be at root level OR nested in items.data[0]
          const itemData = this.stripeSubscription.items?.data?.[0];

          let startTimestamp: any;
          let endTimestamp: any;

          // If in trial period, use trial dates
          if (this.stripeSubscription.status === 'trialing') {
            startTimestamp =
              this.stripeSubscription.trial_start ||
              this.stripeSubscription.created;
            endTimestamp = this.stripeSubscription.trial_end;
          } else {
            startTimestamp =
              this.stripeSubscription.current_period_start ||
              itemData?.current_period_start ||
              this.stripeSubscription.start_date ||
              this.stripeSubscription.created;
            endTimestamp =
              this.stripeSubscription.current_period_end ||
              itemData?.current_period_end ||
              this.stripeSubscription.cancel_at ||
              this.stripeSubscription.ended_at;
          }

          if (startTimestamp) {
            const startDate = new Date(
              typeof startTimestamp === 'number'
                ? startTimestamp * 1000
                : startTimestamp,
            );
            this.subscriptionDate = !isNaN(startDate.getTime())
              ? startDate.toLocaleDateString()
              : '';
          }

          if (endTimestamp) {
            const endDate = new Date(
              typeof endTimestamp === 'number'
                ? endTimestamp * 1000
                : endTimestamp,
            );
            this.subscriptionEndDate = !isNaN(endDate.getTime())
              ? endDate.toLocaleDateString()
              : '';
          }

          // Set cancelled date (when user initiated cancellation)
          const cancelledTimestamp = this.stripeSubscription.canceled_at;
          if (cancelledTimestamp) {
            const cancelledDateObj = new Date(
              typeof cancelledTimestamp === 'number'
                ? cancelledTimestamp * 1000
                : cancelledTimestamp,
            );
            this.cancelledDate = !isNaN(cancelledDateObj.getTime())
              ? cancelledDateObj.toLocaleDateString()
              : '';
          }

          console.log(
            'Stripe subscription full data:',
            this.stripeSubscription,
          );
          console.log('Subscription dates:', {
            startTimestamp,
            endTimestamp,
            canceled_at: this.stripeSubscription.canceled_at,
            cancel_at: this.stripeSubscription.cancel_at,
            current_period_end: this.stripeSubscription.current_period_end,
            subscriptionDate: this.subscriptionDate,
            subscriptionEndDate: this.subscriptionEndDate,
            cancelledDate: this.cancelledDate,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      this.isLoadingSubscription = false;
    }
  };

  formatPrice(amount: number, currency: string): string {
    if (amount == null) return '';
    const value = amount / 100;
    const symbol =
      currency?.toUpperCase() === 'EUR'
        ? '€'
        : currency?.toUpperCase() === 'USD'
          ? '$'
          : currency;
    return `${symbol}${value.toFixed(2)}`;
  }

  formatDate(timestamp: number): string {
    if (!timestamp) return '-';
    const date = new Date(
      typeof timestamp === 'number' ? timestamp * 1000 : timestamp,
    );
    return !isNaN(date.getTime()) ? date.toLocaleDateString() : '-';
  }

  getSubscriptionStartDate(item: any): string {
    // current_period_start can be at root level OR nested in items.data[0]
    const itemData = item.items?.data?.[0];
    const timestamp =
      item.current_period_start ||
      itemData?.current_period_start ||
      item.start_date ||
      item.created;
    return this.formatDate(timestamp);
  }

  getSubscriptionEndDate(item: any): string {
    // current_period_end can be at root level OR nested in items.data[0]
    const itemData = item.items?.data?.[0];
    const timestamp =
      item.current_period_end ||
      itemData?.current_period_end ||
      item.cancel_at ||
      item.ended_at;
    return this.formatDate(timestamp);
  }

  getSubscriptionPlanName(item: any): string {
    // Plan name can be in multiple locations depending on Stripe API version
    const itemData = item.items?.data?.[0];

    // First, try to get the price ID and look it up in our plans
    const priceId = itemData?.price?.id || itemData?.plan?.id || item.plan?.id;
    if (priceId) {
      const allPlans = this.paymentService.getAllPlans();
      const matchedPlan = allPlans.find((p: any) => p.id === priceId);
      if (matchedPlan) {
        return matchedPlan.name;
      }
    }

    // Fallback: Check price.nickname (newer Stripe API), then plan.nickname
    return (
      itemData?.price?.nickname ||
      itemData?.plan?.nickname ||
      item.plan?.nickname ||
      'Subscription'
    );
  }

  translateInterval(interval: string): string {
    if (this.localeService.getLanguage() !== 'english') {
      return interval === 'year' ? 'anno' : 'mese';
    }
    return interval || 'month';
  }

  getSubscriptionAmount(item: any): string {
    const itemData = item.items?.data?.[0];
    const price = itemData?.price || itemData?.plan || item.plan;
    const rawInterval =
      price?.recurring?.interval || price?.interval || 'month';
    const currency = item.latest_invoice?.currency || price?.currency;
    const isItalian = this.localeService.getLanguage() !== 'english';

    const intervalLabel = isItalian
      ? rawInterval === 'year'
        ? 'anno'
        : 'mese'
      : rawInterval === 'year'
        ? 'year'
        : 'month';

    // Use invoice amount only when > 0 (amount_paid = 0 means a 100% promo was applied;
    // in that case show the plan's base price so the UI is meaningful)
    const invoiceAmount = item.latest_invoice?.amount_paid;
    if (invoiceAmount != null && invoiceAmount > 0) {
      return `${this.formatPrice(invoiceAmount, currency)}/${intervalLabel}`;
    }

    // Fallback to plan base price
    if (!price) return '';
    const amount = price.unit_amount || price.amount;
    if (!amount) return '';
    return `${this.formatPrice(amount, currency)}/${intervalLabel}`;
  }

  getStatusBadgeClass(): string {
    if (!this.stripeSubscription) return 'bg-gray-500';
    if (this.stripeSubscription.cancel_at_period_end) return 'bg-orange-500';
    switch (this.stripeSubscription.status) {
      case 'active':
        return 'bg-[#043B22]';
      case 'past_due':
        return 'bg-red-500';
      case 'canceled':
        return 'bg-gray-500';
      case 'trialing':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  }

  getStatusTextClass(): string {
    if (!this.stripeSubscription) return 'text-gray-600';
    if (this.stripeSubscription.cancel_at_period_end) return 'text-orange-600';
    switch (this.stripeSubscription.status) {
      case 'active':
        return 'text-green-600';
      case 'past_due':
        return 'text-red-600';
      case 'canceled':
        return 'text-gray-600';
      case 'trialing':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  }

  getStatusLabel(): string {
    if (!this.stripeSubscription) return '';
    const lang = this.localeService.getLanguage();
    if (this.stripeSubscription.cancel_at_period_end) {
      return lang === 'english' ? 'Cancelled' : 'Cancellato';
    }
    switch (this.stripeSubscription.status) {
      case 'active':
        return lang === 'english' ? 'Active' : 'Attivo';
      case 'past_due':
        return lang === 'english' ? 'Past Due' : 'Scaduto';
      case 'canceled':
        return lang === 'english' ? 'Cancelled' : 'Cancellato';
      case 'trialing':
        return lang === 'english' ? 'Trial' : 'Prova';
      default:
        return this.stripeSubscription.status;
    }
  }

  getHistoryStatusLabel(item: any): string {
    const lang = this.localeService.getLanguage();
    if (item.cancel_at_period_end)
      return lang === 'english' ? 'Cancelled' : 'Cancellato';
    switch (item.status) {
      case 'active':
        return lang === 'english' ? 'Active' : 'Attivo';
      case 'past_due':
        return lang === 'english' ? 'Past Due' : 'Scaduto';
      case 'canceled':
        return lang === 'english' ? 'Cancelled' : 'Cancellato';
      case 'trialing':
        return lang === 'english' ? 'Trial' : 'Prova';
      default:
        return item.status;
    }
  }

  cancelSubscription = async () => {
    try {
      this.spinner.show();
      this.showCancelModal = false;

      // Get user email to identify customer in Stripe
      const userEmail = (await fetchUserAttributes()).email;

      // Get Cognito auth token
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      if (!authToken) {
        throw new Error('Not authenticated');
      }

      // Store plan info before cancellation for email
      const currentPlanName =
        this.selectedPlan?.name ||
        this.stripeSubscription?.plan?.nickname ||
        'Subscription';
      const endDate =
        this.subscriptionEndDate || new Date().toLocaleDateString();

      // Call Lambda Function URL to cancel subscription in Stripe (sets cancel_at_period_end)
      const response = await fetch(environment.lambdaUrls.stripe, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          email: userEmail,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      // DO NOT clear subscriptionPlan from DynamoDB here.
      // The user keeps access until the billing period ends.
      // The Lambda webhook for customer.subscription.deleted will
      // clear subscriptionPlan when Stripe actually terminates the subscription.

      // Send subscription cancellation email (non-blocking)
      if (userEmail) {
        this.emailService.sendSubscriptionCancelEmail(userEmail, {
          planName: currentPlanName,
          endDate: endDate,
        });
      }

      // Refresh user data
      await this.userService.getUser();

      // Refresh subscription data from Stripe to show updated status
      await this.fetchStripeSubscription();

      this.spinner.hide();
      this.toastr.success(this.locale.common.subscriptionCancelled);
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      this.spinner.hide();

      // Check if it's a network error (backend not deployed)
      if (error.message === 'Failed to fetch') {
        this.toastr.error(
          'Subscription cancellation is not available yet. Please contact support.',
        );
      } else {
        this.toastr.error(this.locale.common.cancelSubscriptionError);
      }
    }
  };

  selectPlan = async (plan: any) => {
    // Guard: ensure fiscal data required for invoicing is complete before proceeding
    const missingFields: string[] = [];
    if (!this.form.controls['companyName'].value?.trim())
      missingFields.push(
        this.localeService.getLanguage() === 'english'
          ? 'Company Name'
          : 'Nome azienda',
      );
    if (!this.form.controls['vatNumber'].value?.trim())
      missingFields.push('P.IVA / VAT');
    if (!this.form.controls['pec'].value?.trim()) missingFields.push('PEC');
    if (!this.form.controls['street'].value?.trim())
      missingFields.push(
        this.localeService.getLanguage() === 'english'
          ? 'Street'
          : 'Via / Indirizzo',
      );
    if (!this.form.controls['city'].value?.trim())
      missingFields.push(
        this.localeService.getLanguage() === 'english' ? 'City' : 'Città',
      );
    if (!this.form.controls['postalCode'].value?.trim())
      missingFields.push(
        this.localeService.getLanguage() === 'english' ? 'Postal Code' : 'CAP',
      );

    if (missingFields.length > 0) {
      const msg =
        this.localeService.getLanguage() === 'english'
          ? `Please complete your billing profile before subscribing. Missing: ${missingFields.join(', ')}.`
          : `Completa i dati di fatturazione prima di procedere. Mancanti: ${missingFields.join(', ')}.`;
      this.toastr.error(msg, '', { timeOut: 6000 });
      return;
    }

    this.pendingPlanId = plan.id;
    this.contractAccepted = false;
    const street = this.form.controls.street.value ?? '';
    const postalCode = this.form.controls.postalCode.value ?? '';
    const city = this.form.controls.city.value ?? '';
    const province = this.form.controls.province.value ?? '';
    const addressParts = [
      street,
      [postalCode, city].filter(Boolean).join(' '),
      province ? `(${province})` : '',
    ].filter(Boolean);
    this.contractForm.patchValue({
      companyName: this.currentUser?.companyName ?? '',
      address: addressParts.join(', '),
      vatNumber: this.currentUser?.vatNumber ?? '',
      pec: this.currentUser?.pec ?? '',
      legalRepresentative: this.currentUser?.legalRepresentative ?? '',
    });
    this.showContractModal = true;
  };

  confirmSubscription = async () => {
    if (!this.contractAccepted || this.contractForm.invalid) return;
    this.showContractModal = false;
    this.spinner.show();
    try {
      // Sync contract form values into the main form, then save via userService
      this.form.controls.companyName.setValue(
        this.contractForm.controls.companyName.value ?? '',
      );
      this.form.controls.vatNumber.setValue(
        this.contractForm.controls.vatNumber.value ?? '',
      );
      this.form.controls.pec.setValue(
        this.contractForm.controls.pec.value ?? '',
      );
      this.form.controls.legalRepresentative.setValue(
        this.contractForm.controls.legalRepresentative.value ?? '',
      );
      await this.userService.updateUser(this.form);
    } catch (error) {
      console.error('Error saving contract data:', error);
    }
    this.spinner.hide();
    this.subscribe(this.pendingPlanId);
  };

  getContractPlaceAndDate(): string {
    const city = this.form.controls.city.value || '';
    const now = new Date();
    const date = now.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return city ? city + ', ' + date : date;
  }

  subscribe = async (priceId: string) => {
    this.spinner.show();

    try {
      const userEmail = (await fetchUserAttributes()).email;
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        this.toastr.error('Authentication error');
        this.spinner.hide();
        return;
      }

      // Create checkout session via Lambda (reuses existing customer)
      const response = await fetch(environment.lambdaUrls.stripe, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: userEmail,
          action: 'create_checkout_session',
          priceId,
          successUrl: `${environment.baseUrl}/dashboard/profile?subscribed=${priceId}&id=${this.form.controls.id.value}`,
          cancelUrl: `${environment.baseUrl}/dashboard/profile?subscribed=n`,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.url) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = result.url;
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      this.toastr.error(error.message || 'An error occurred');
      this.spinner.hide();
    }
  };
}
